/**
 * Carregamento do índice e revalidação — o mesmo ciclo da fumaça.
 *
 * Só baixa quando a camada é ligada. Depois, revalida:
 * - a cada ciclo da atualização automática (gancho do loop);
 * - quando a janela do loop anda (chegou imagem nova do satélite).
 * O índice tem ~5 KB comprimido e a máquina responde 304 quando nada mudou.
 */
import { subscribeSelector } from '../lib/store';
import { useMemo } from 'react';
import { formatTimeBrt } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { BASE_GSMAP, carregarIndice, type IndiceGsmap, type QuadroGsmap } from './api';
import { useGsmapStore } from './store';
import { avisoDeFrescor, quadroParaInstante } from './tempo';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;
/** A camada registra aqui o "tente de novo os que falharam". */
let tentarDeNovo: (() => void) | null = null;

export function registrarNovaTentativa(fn: (() => void) | null): void {
  tentarDeNovo = fn;
}

function mesmoIndice(a: IndiceGsmap | null, b: IndiceGsmap): boolean {
  if (!a || a.quadros.length !== b.quadros.length) return false;
  // Grade ou codificação nova (formato novo): os bytes guardados não servem mais.
  const ga = a.grade;
  const gb = b.grade;
  if (ga.lat0 !== gb.lat0 || ga.lon0 !== gb.lon0 || ga.passo !== gb.passo || ga.ni !== gb.ni || ga.nj !== gb.nj) return false;
  if (a.codificacao.fator !== b.codificacao.fator || a.codificacao.semDado !== b.codificacao.semDado) return false;
  return a.quadros.every((q, i) => q.c === b.quadros[i].c && q.p === b.quadros[i].p);
}

export async function carregarGsmap(force = false): Promise<void> {
  if (!BASE_GSMAP) return;
  const { situacao } = useGsmapStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  // A revalidação é silenciosa: "Baixando…" piscando a cada ciclo é ruído.
  if (!jaCarregou) useGsmapStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const indice = await carregarIndice(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Mesmos quadros: não trocar poupa replanejar a camada.
    const atual = useGsmapStore.getState().indice;
    useGsmapStore.setState({
      indice: mesmoIndice(atual, indice) ? { ...atual!, gerado: indice.gerado } : indice,
      situacao: { estado: 'ready', erro: null },
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useGsmapStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com a camada em uso. */
export function refreshGsmap(): void {
  if (!useGsmapStore.getState().visivel) return;
  tentarDeNovo?.();
  const nuncaCarregou = !jaCarregou && useGsmapStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarGsmap(true);
}

export function iniciarSyncGsmap(): () => void {
  if (useGsmapStore.getState().visivel) void carregarGsmap();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshGsmap();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useGsmapStore.subscribe((a, b) => {
      if (!a.visivel || b.visivel) return;
      // Religar depois de um tempo: o índice guardado pode ser de horas atrás.
      if (!jaCarregou) void carregarGsmap();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarGsmap(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshGsmap),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Fim da hora mais nova publicada, ou null. */
export function fimDoUltimo(indice: IndiceGsmap | null): number | null {
  const q = indice?.quadros;
  return q?.length ? q[q.length - 1].fim : null;
}

/** A hora casada com o instante exibido (painel, legenda, resumo do trilho). */
export function useHoraNaTela(): QuadroGsmap | null {
  const indice = useGsmapStore((s) => s.indice);
  const instante = useTimeStore((s) => shownTimeOf(s));
  return useMemo(() => {
    const quadros = indice?.quadros ?? [];
    return quadroParaInstante(
      quadros,
      quadros.map((x) => x.t),
      instante,
    );
  }, [indice, instante]);
}

/** Resumo no bloco da grade do celular: só a hora ("13:30–14:30"); o card já diz que é a chuva. */
export function useGsmapResumo(): string | null {
  const visivel = useGsmapStore((s) => s.visivel);
  const q = useHoraNaTela();
  if (!visivel) return null;
  return q ? `${formatTimeBrt(q.ini)}–${formatTimeBrt(q.fim)}` : 'sem estimativa';
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useGsmapDesatualizado(): boolean {
  const visivel = useGsmapStore((s) => s.visivel);
  const erro = useGsmapStore((s) => s.situacao.estado === 'error' && !s.indice);
  const ultimo = useGsmapStore((s) => fimDoUltimo(s.indice));
  // Relógio próprio: com a máquina parada o índice não muda.
  const agora = useNow(60_000);
  if (!visivel) return false;
  return erro || avisoDeFrescor(ultimo, agora) !== null;
}

/** Só para os testes. */
export function resetGsmapSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
